// Pure layout-engine types and constants for the meeting "Main area".
//
// The engine is DOM-free: it takes a measured container size + counts and
// returns plain rectangles. All rects are in CSS px, relative to the container
// origin (0,0 = top-left of the Main area).

export interface Size {
    width: number;
    height: number;
}

export interface Rect {
    x: number;
    y: number;
    width: number;
    height: number;
}

/** A placed tile: `index` is the position in the source feed list. */
export interface TilePlacement {
    index: number;
    rect: Rect;
}

// --- Spec constants -------------------------------------------------------

/** Participant feeds must never render narrower than this. */
export const MIN_FEED_WIDTH = 192;
/**
 * Feed min width for the compact dock (thumbnail/sidebar). The dock is only
 * ~276–360px wide, where the full 192px minimum would force a single column;
 * a smaller minimum lets a few faces sit side by side.
 */
export const COMPACT_MIN_FEED_WIDTH = 96;
/** Reserved feed-area width in a horizontal split. */
export const MIN_FEED_AREA_WIDTH = 192;
/** Reserved feed-area height in a vertical split. */
export const MIN_FEED_AREA_HEIGHT = 144;
/** Narrowest allowed feed aspect ratio (w/h). */
export const MIN_ASPECT = 4 / 3;
/** Widest allowed feed aspect ratio (w/h). */
export const MAX_ASPECT = 16 / 9;
/** Default inter-tile / inter-region gap. */
export const DEFAULT_GAP = 8;
/**
 * Fallback stage aspect for a shared screen, used until the real source ratio
 * is measured from the on-stage video (see useReportScreenShareAspect).
 */
export const SCREEN_SHARE_ASPECT = 16 / 9;

// --- Engine I/O -----------------------------------------------------------

export type LayoutMode = 'FEED' | 'SPLIT';
export type SplitOrientation = 'HORIZONTAL' | 'VERTICAL';

export interface LayoutInput {
    /** Measured Main-area box. */
    container: Size;
    /** Number of participant feeds destined for the feed grid / strip. */
    feedCount: number;
    /** Number of pinned feeds (drives a SPLIT when no screen is shared). */
    pinnedCount?: number;
    /** Shared-screen aspect ratio (w/h); null/undefined => no screen share. */
    screenAspect?: number | null;
    gap?: number;
    /** Override the feed min width (defaults to {@link MIN_FEED_WIDTH}). */
    minFeedWidth?: number;
    /**
     * Cap on real feed tiles to place (bounds live video tiles/decodes). The
     * "+N" overflow still counts against the full `feedCount`, so it reflects
     * every feed not shown — whether hidden by the container or this cap.
     */
    maxVisibleFeeds?: number;
    /**
     * Force FEED mode even when a screen is shared or feeds are pinned. Used by
     * the compact dock, which is too small for a meaningful stage + strip split.
     */
    feedOnly?: boolean;
}

export interface FeedGridResult {
    cols: number;
    rows: number;
    tileWidth: number;
    tileHeight: number;
    tileAspect: number;
    /** Number of real feed tiles shown (excludes any "+N" overflow cell). */
    visibleCount: number;
    /** Feeds not shown; rendered as a single "+N" cell when > 0. */
    overflowCount: number;
    /**
     * Placed cells. When `overflowCount > 0` the LAST entry is the "+N" cell;
     * entries `0..visibleCount-1` map to the source feeds by `index`.
     */
    tiles: TilePlacement[];
}

export interface LayoutResult {
    mode: LayoutMode;
    /** SPLIT only. */
    orientation?: SplitOrientation;
    /** SPLIT only: the stage box (shared screen or pinned sub-grid bbox). */
    stage?: Rect;
    /** SPLIT + pinned-driven only: pinned tiles placed inside the stage. */
    pinnedTiles?: TilePlacement[];
    /** SPLIT only: the feed strip region. */
    feedArea?: Rect;
    /** FEED: all tiles. SPLIT: the strip tiles. */
    feedTiles: TilePlacement[];
    feedTileAspect: number;
    visibleFeedCount: number;
    overflowCount: number;
}

// --- Small shared helpers -------------------------------------------------

export const clamp = (value: number, lo: number, hi: number): number =>
    Math.min(hi, Math.max(lo, value));

/** Largest rect of the given aspect (w/h) that fits inside box (w×h). */
export const fitAspectInBox = (
    boxWidth: number,
    boxHeight: number,
    aspect: number,
): Size => {
    if (boxWidth / boxHeight > aspect) {
        // Box is wider than the target ratio => height-bound.
        const height = boxHeight;
        return { width: height * aspect, height };
    }
    const width = boxWidth;
    return { width, height: width / aspect };
};
